import { useEffect, useState } from 'react';
import { FiGift, FiCopy, FiCheck, FiUsers, FiShare2, FiGlobe, FiMessageSquare, FiActivity, FiTrendingUp, FiAward, FiZap } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import { getUserInviteStats } from '../utils/api';
import { useI18n } from '../i18n/useI18n';

/**
 * Telegram 图标组件
 */
function TelegramIcon({ className }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
    >
      <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.562 8.161c-.18 1.897-.962 6.502-1.359 8.627-.168.9-.5 1.201-.82 1.23-.697.064-1.226-.461-1.901-.903-1.056-.693-1.653-1.124-2.678-1.8-1.185-.781-.417-1.21.258-1.911.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.248-.024c-.106.024-1.793 1.139-5.062 3.345-.479.329-.913.489-1.302.481-.428-.009-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.831-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.477-1.635.099-.002.321.023.465.14.121.098.154.23.17.324.015.094.035.308.02.476z" />
    </svg>
  );
}

export function InviteManagePage({ pushToast }) {
  const { userId, isAuthenticated } = useAuth();
  const { t, lang } = useI18n();
  const locale = lang === 'zh' ? 'zh-CN' : 'en-US';
  const [loading, setLoading] = useState(true);
  const [inviteData, setInviteData] = useState(null);
  const [copiedType, setCopiedType] = useState(null);

  const botUsername = import.meta.env.VITE_BOT_USERNAME || 'PolyOneAI_bot';

  useEffect(() => {
    if (!isAuthenticated || !userId) {
      setLoading(false);
      return;
    }

    getUserInviteStats(userId)
      .then(data => {
        if (data.code === 0) {
          setInviteData(data.data);
        }
      })
      .catch(err => {
        console.error('获取邀请数据失败:', err);
      })
      .finally(() => setLoading(false));
  }, [userId, isAuthenticated]);

  const handleCopyInviteLink = (type) => {
    if (!inviteData?.invite_code) return;

    const code = inviteData.invite_code;
    let link;

    if (type === 'telegram') {
      link = `https://t.me/${botUsername}?start=${code}`;
    } else {
      link = `${window.location.origin}/invite/${code}`;
    }

    navigator.clipboard.writeText(link).then(() => {
      setCopiedType(type);
      pushToast && pushToast(t('inviteManage.copiedLink'), 'success');
      setTimeout(() => setCopiedType(null), 2000);
    });
  };

  const handleCopyInviteMessage = () => {
    if (!inviteData?.invite_code) return;

    const code = inviteData.invite_code;
    const telegramLink = `https://t.me/${botUsername}?start=${code}`;
    const webLink = `${window.location.origin}/invite/${code}`;

    const message = t('inviteManage.messageTemplate', {
      brand: 'PolyOne',
      code,
      telegramLink,
      webLink,
    });

    navigator.clipboard.writeText(message).then(() => {
      setCopiedType('message');
      pushToast && pushToast(t('inviteManage.copiedMessage'), 'success');
      setTimeout(() => setCopiedType(null), 2000);
    });
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center p-6">
        <div className="glass-xl w-full max-w-md p-10 text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center border border-slate-700/50 shadow-xl">
            <FiGift className="w-10 h-10 text-sky-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-3">{t('inviteManage.loginRequiredTitle')}</h2>
          <p className="text-slate-400 text-sm max-w-xs mx-auto">{t('inviteManage.loginRequiredDesc')}</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center p-6">
        <div className="glass-xl w-full max-w-md p-10 text-center">
          <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-sky-500/10 flex items-center justify-center">
            <div className="w-8 h-8 border-4 border-sky-500/30 border-t-sky-500 rounded-full animate-spin"></div>
          </div>
          <p className="text-slate-400 animate-pulse">{t('inviteManage.loading')}</p>
        </div>
      </div>
    );
  }

  if (!inviteData || !inviteData.has_invite_code) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center p-6">
        <div className="glass-xl w-full max-w-md p-10 text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center border border-slate-700/50 shadow-xl">
            <FiGift className="w-10 h-10 text-slate-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-3">{t('inviteManage.noCodeTitle')}</h2>
          <p className="text-slate-400 text-sm">{t('inviteManage.noCodeDesc')}</p>
        </div>
      </div>
    );
  }

  if (!inviteData.can_invite) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center p-6">
        <div className="glass-xl w-full max-w-md p-10 text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-amber-500/10 flex items-center justify-center border border-amber-500/30">
            <FiAward className="w-10 h-10 text-amber-500" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-3">{t('inviteManage.unavailableTitle')}</h2>
          <p className="text-slate-400 text-sm">{t('inviteManage.unavailableDesc')}</p>
        </div>
      </div>
    );
  }

  return (
    <section className="glass-lg w-full overflow-hidden min-h-[80vh]">
      <div className="p-6 md:p-8 lg:p-10">
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2.5 bg-gradient-to-br from-sky-500 to-blue-600 rounded-xl shadow-lg shadow-sky-500/20">
                  <FiShare2 className="w-6 h-6 text-white" />
                </div>
                <h1 className="text-2xl md:text-3xl font-bold text-white">{t('inviteManage.pageTitle')}</h1>
              </div>
              <p className="text-slate-400 text-sm max-w-lg">
                {t('inviteManage.pageDesc', { brand: 'PolyOne' })}
              </p>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
              <span className="text-sm text-emerald-400 font-medium">{t('copyTrading.systemOnline')}</span>
            </div>
          </div>
        </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        {/* Invite Code Card */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-sky-500/10 to-blue-600/5 border border-sky-500/20 p-6">
          <div className="absolute top-0 right-0 p-4 opacity-10">
            <FiGift className="w-20 h-20 text-sky-400" />
          </div>
          <p className="text-xs text-sky-400/80 font-medium uppercase tracking-wider mb-3">{t('inviteManage.yourCode')}</p>
          <div className="flex items-center gap-3 mb-3">
            <span className="text-3xl md:text-4xl font-mono font-bold text-white tracking-wider">{inviteData.invite_code}</span>
            <button
              onClick={() => {
                navigator.clipboard.writeText(inviteData.invite_code);
                pushToast(t('inviteManage.codeCopied'), 'success');
              }}
              className="p-2 rounded-lg bg-sky-500/20 text-sky-400 hover:bg-sky-500/30 hover:text-white transition-all"
              title={t('inviteManage.copy')}
            >
              <FiCopy className="w-4 h-4" />
            </button>
          </div>
          <p className="text-xs text-slate-500">{t('inviteManage.yourCredential')}</p>
        </div>

        {/* Invited Count Card */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 border border-emerald-500/20 p-6">
          <div className="absolute top-0 right-0 p-4 opacity-10">
            <FiUsers className="w-20 h-20 text-emerald-400" />
          </div>
          <p className="text-xs text-emerald-400/80 font-medium uppercase tracking-wider mb-3">{t('inviteManage.invitedFriends')}</p>
          <div className="text-3xl md:text-4xl font-bold text-white mb-1">{inviteData.total_invitations || 0}</div>
          <p className="text-xs text-slate-500">{t('inviteManage.invitedCountDesc')}</p>
        </div>

        {/* Activity Card */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-violet-500/10 to-purple-600/5 border border-violet-500/20 p-6">
          <div className="absolute top-0 right-0 p-4 opacity-10">
            <FiTrendingUp className="w-20 h-20 text-violet-400" />
          </div>
          <p className="text-xs text-violet-400/80 font-medium uppercase tracking-wider mb-3">{t('inviteManage.activity') || '邀请活跃度'}</p>
          <div className="text-3xl md:text-4xl font-bold text-white mb-1">
            {inviteData.invitations && inviteData.invitations.length > 0 ? 'High' : 'New'}
          </div>
          <p className="text-xs text-slate-500">{t('inviteManage.keepSharing') || '继续分享获得更多奖励'}</p>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left Column: Share Methods */}
        <div className="lg:col-span-5 space-y-4">
          <div className="flex items-center gap-2 mb-4">
            <FiZap className="w-5 h-5 text-amber-400" />
            <h2 className="text-lg font-bold text-white">{t('inviteManage.shareMethods')}</h2>
          </div>

          {/* Telegram Share */}
          <button
            onClick={() => handleCopyInviteLink('telegram')}
            className="w-full group relative p-5 rounded-2xl bg-gradient-to-r from-[#0088cc]/10 to-[#0088cc]/5 border border-[#0088cc]/20 hover:border-[#0088cc]/40 transition-all text-left overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-[#0088cc]/0 to-[#0088cc]/5 opacity-0 group-hover:opacity-100 transition-opacity"></div>
            <div className="relative flex items-start gap-4">
              <div className="p-3 rounded-xl bg-[#0088cc]/20 text-[#0088cc]">
                <TelegramIcon className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <h3 className="text-white font-bold">{t('inviteManage.telegramInvite')}</h3>
                  <div className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${copiedType === 'telegram' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-800/50 text-slate-400 group-hover:text-white'}`}>
                    {copiedType === 'telegram' ? <span className="flex items-center gap-1"><FiCheck className="w-3 h-3" /> {t('inviteManage.copied')}</span> : t('inviteManage.copyLink')}
                  </div>
                </div>
                <p className="text-xs text-slate-400">{t('inviteManage.telegramInviteDesc')}</p>
              </div>
            </div>
          </button>

          {/* Web Share */}
          <button
            onClick={() => handleCopyInviteLink('web')}
            className="w-full group relative p-5 rounded-2xl bg-gradient-to-r from-slate-800/60 to-slate-800/40 border border-slate-700/50 hover:border-slate-600 transition-all text-left overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-slate-700/0 to-slate-700/20 opacity-0 group-hover:opacity-100 transition-opacity"></div>
            <div className="relative flex items-start gap-4">
              <div className="p-3 rounded-xl bg-slate-700/50 text-slate-300">
                <FiGlobe className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <h3 className="text-white font-bold">{t('inviteManage.webInvite')}</h3>
                  <div className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${copiedType === 'web' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-800/50 text-slate-400 group-hover:text-white'}`}>
                    {copiedType === 'web' ? <span className="flex items-center gap-1"><FiCheck className="w-3 h-3" /> {t('inviteManage.copied')}</span> : t('inviteManage.copyLink')}
                  </div>
                </div>
                <p className="text-xs text-slate-400">{t('inviteManage.webInviteDesc')}</p>
              </div>
            </div>
          </button>

          {/* Message Share */}
          <button
            onClick={handleCopyInviteMessage}
            className="w-full group relative p-5 rounded-2xl bg-gradient-to-r from-violet-500/10 to-purple-500/5 border border-violet-500/20 hover:border-violet-500/40 transition-all text-left overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-violet-500/0 to-violet-500/10 opacity-0 group-hover:opacity-100 transition-opacity"></div>
            <div className="relative flex items-start gap-4">
              <div className="p-3 rounded-xl bg-violet-500/20 text-violet-400">
                <FiMessageSquare className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <h3 className="text-white font-bold">{t('inviteManage.messageInvite')}</h3>
                  <div className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${copiedType === 'message' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-800/50 text-slate-400 group-hover:text-white'}`}>
                    {copiedType === 'message' ? <span className="flex items-center gap-1"><FiCheck className="w-3 h-3" /> {t('inviteManage.copied')}</span> : t('inviteManage.copyText')}
                  </div>
                </div>
                <p className="text-xs text-slate-400">{t('inviteManage.messageInviteDesc')}</p>
              </div>
            </div>
          </button>
        </div>

        {/* Right Column: Invite History */}
        <div className="lg:col-span-7">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <FiActivity className="w-5 h-5 text-sky-400" />
              <h2 className="text-lg font-bold text-white">{t('inviteManage.historyTitle')}</h2>
            </div>
            <span className="text-xs px-3 py-1 rounded-full bg-slate-800 text-slate-400 border border-slate-700">
              {t('inviteManage.recentN', { count: Math.min(inviteData.invitations?.length || 0, 10) })}
            </span>
          </div>

          <div className="bg-slate-900/40 rounded-2xl border border-slate-800/50 overflow-hidden">
            {inviteData.invitations && inviteData.invitations.length > 0 ? (
              <div className="divide-y divide-slate-800/50">
                {inviteData.invitations.slice(0, 10).map((invite, index) => (
                  <div key={index} className="p-4 flex items-center justify-between hover:bg-slate-800/30 transition-colors">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-slate-700 to-slate-800 flex items-center justify-center text-slate-300 font-mono text-sm border border-slate-700 shadow-inner">
                        #{index + 1}
                      </div>
                      <div>
                        <div className="text-sm font-bold text-slate-200 font-mono tracking-wide">
                          {invite.invitee_id ? `${invite.invitee_id.substring(0, 6)}...${invite.invitee_id.substring(invite.invitee_id.length - 4)}` : t('inviteManage.unknownUser')}
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5">
                          {new Date(invite.created_at).toLocaleString(locale)}
                        </div>
                      </div>
                    </div>

                    <div className={`px-3 py-1.5 rounded-full text-xs font-medium border flex items-center gap-1.5 ${invite.source === 'telegram'
                        ? 'bg-[#0088cc]/10 text-[#0088cc] border-[#0088cc]/20'
                        : 'bg-slate-700/30 text-slate-400 border-slate-600/30'
                      }`}>
                      {invite.source === 'telegram' ? <TelegramIcon className="w-3 h-3" /> : <FiGlobe className="w-3 h-3" />}
                      {invite.source === 'telegram' ? 'Telegram' : 'Web'}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-16 text-center p-8">
                <div className="w-16 h-16 rounded-2xl bg-slate-800/50 flex items-center justify-center mb-4 border border-slate-700/50">
                  <FiActivity className="w-8 h-8 text-slate-600" />
                </div>
                <p className="text-slate-300 font-medium mb-1">{t('inviteManage.noInvites')}</p>
                <p className="text-sm text-slate-500">{t('inviteManage.noInvitesHint')}</p>
              </div>
            )}
          </div>
        </div>
      </div>
      </div>
    </section>
  );
}
